import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaMicrophone, FaMicrophoneSlash } from "react-icons/fa";
import ParticipantCameraOffBox from "components/ui-components/ParticipantCameraOffBox";
import PropTypes from "prop-types";

/* ── Web Audio speaking detection ──────────────────────────────────────────── */
export const useSpeakingDetection = (stream, isAudioOn) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);
  const speakingRef = useRef(false);
  const silenceRef = useRef(0);

  useEffect(() => {
    if (!stream || !isAudioOn) { setIsSpeaking(false); return; }
    if (!stream.getAudioTracks().length) return;

    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;

    let active = true;
    try {
      const ctx = new AudioCtx();
      ctxRef.current = ctx;
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.85;
      src.connect(analyser);

      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!active) return;
        analyser.getByteFrequencyData(data);
        const avg = data.reduce((s, v) => s + v, 0) / data.length;
        const loud = avg > 28;
        if (loud) {
          silenceRef.current = 0;
          if (!speakingRef.current) { speakingRef.current = true; setIsSpeaking(true); }
        } else {
          silenceRef.current += 1;
          if (speakingRef.current && silenceRef.current > 40) {
            speakingRef.current = false;
            setIsSpeaking(false);
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { console.warn("[Speaking]", e); }

    return () => {
      active = false;
      cancelAnimationFrame(rafRef.current);
      speakingRef.current = false;
      silenceRef.current = 0;
      try { ctxRef.current?.close(); } catch { }
      ctxRef.current = null;
      setIsSpeaking(false);
    };
  }, [stream, isAudioOn]);

  return isSpeaking;
};

/* ── Styles ──────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  .pgv-tile {
    position: relative;
    width: 100%;
    height: 100%;
    border-radius: 12px;
    overflow: hidden;
    background: #0c1428;
    border: 2px solid #1a2540;
    font-family: 'Inter', system-ui, sans-serif;
  }

  /* Speaking ring lives on its own div — pgv-tile class never changes
     so the <video> subtree never repaints when speaking toggles */
  .pgv-ring {
    position: absolute;
    inset: -2px;
    border-radius: 13px;
    border: 2px solid transparent;
    pointer-events: none;
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
    z-index: 10;
  }

  .pgv-ring.pgv-speaking {
    border-color: #4ade80;
    box-shadow: 0 0 0 2px rgba(74,222,128,0.18), 0 0 20px rgba(74,222,128,0.15);
    animation: pgvPulse 1.8s ease-in-out infinite;
  }

  @keyframes pgvPulse {
    0%, 100% { box-shadow: 0 0 0 2px rgba(74,222,128,0.15), 0 0 16px rgba(74,222,128,0.10); }
    50%       { box-shadow: 0 0 0 3px rgba(74,222,128,0.35), 0 0 28px rgba(74,222,128,0.22); }
  }

  .pgv-video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .pgv-mic {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: rgba(8, 13, 28, 0.78);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 1px solid rgba(255,255,255,0.10);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
  }

  .pgv-bar {
    position: absolute;
    bottom: 0; left: 0; right: 0;
    padding: 32px 10px 9px;
    background: linear-gradient(to top, rgba(5,9,22,0.92) 0%, rgba(5,9,22,0.4) 55%, transparent 100%);
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 6px;
  }

  .pgv-name {
    font-size: 12.5px;
    font-weight: 600;
    color: #e8eeff;
    text-shadow: 0 1px 6px rgba(0,0,0,0.8);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    flex: 1;
  }

  .pgv-eq {
    display: flex;
    align-items: flex-end;
    gap: 2.5px;
    height: 14px;
    flex-shrink: 0;
  }

  .pgv-eq span {
    display: block;
    width: 3px;
    border-radius: 2px;
    background: #4ade80;
    animation: pgvEq 0.58s ease-in-out infinite;
  }
  .pgv-eq span:nth-child(1) { height: 5px;  animation-delay: 0s; }
  .pgv-eq span:nth-child(2) { height: 11px; animation-delay: 0.12s; }
  .pgv-eq span:nth-child(3) { height: 7px;  animation-delay: 0.24s; }
  .pgv-eq span:nth-child(4) { height: 9px;  animation-delay: 0.08s; }

  @keyframes pgvEq {
    0%, 100% { transform: scaleY(0.45); opacity: 0.7; }
    50%       { transform: scaleY(1.1);  opacity: 1;   }
  }

  .pgv-reconnecting {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(251, 191, 36, 0.92);
    color: #1a1a1a;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 20px;
    z-index: 20;
    white-space: nowrap;
    pointer-events: none;
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

/* ── Component ───────────────────────────────────────────────────────────── */
const ParticipantGridVideo = ({
  userId, remoteStream, userNameValue,
  isAudioOn, isCameraOn, colorCode, latter, isLgIcon = false,
  connectionQuality,
}) => {
  inject();
  const videoRef = useRef(null);
  const remoteStreamRef = useRef(remoteStream);
  const isSpeaking = useSpeakingDetection(remoteStream, isAudioOn);

  // Keep ref in sync with prop — always current, no stale closure
  remoteStreamRef.current = remoteStream;

  // Case A: <video> mounts AFTER stream arrived (e.g. isCameraOn was false at
  // stream time). The stable callback reads remoteStreamRef so it always has
  // the latest stream, but never gets recreated → no remount blink.
  const videoCallback = useCallback((node) => {
    videoRef.current = node;
    if (node && remoteStreamRef.current) {
      node.srcObject = remoteStreamRef.current;
      node.play().catch(() => { });
    }
  }, []); // intentionally empty — stability is the point

  // Case B: stream arrives/changes while <video> is already mounted
  useEffect(() => {
    if (videoRef.current && remoteStream) {
      videoRef.current.srcObject = remoteStream;
      videoRef.current.play().catch(() => { });
    }
  }, [remoteStream]);

  return (
    <div className="pgv-tile">

      {/* Speaking ring — own div, pgv-tile never changes class → no video repaint */}
      <div className={`pgv-ring${isSpeaking && isAudioOn ? " pgv-speaking" : ""}`} />

      {connectionQuality === "poor" && (
        <div className="pgv-reconnecting">Reconnecting...</div>
      )}

      {/* The <video> element MUST stay mounted at all times — even when camera is off.
           It is the only audio output for the remoteStream. Unmounting it destroys
           the srcObject binding and silences audio. Hidden with display:none instead. */}
      <video
        ref={videoCallback}
        id={`videoX_${userId}`}
        className="pgv-video"
        autoPlay
        playsInline
        data-username={userId}
        style={{ display: isCameraOn ? "block" : "none" }}
      />

      {isCameraOn && (
        <>
          <div className="pgv-mic">
            {isAudioOn
              ? <FaMicrophone style={{ color: "#8b9dc3" }} />
              : <FaMicrophoneSlash style={{ color: "#f87171" }} />}
          </div>
          <div className="pgv-bar">
            <div className="pgv-name">{userNameValue}</div>
            {isSpeaking && isAudioOn && (
              <div className="pgv-eq">
                <span /><span /><span /><span />
              </div>
            )}
          </div>
        </>
      )}

      {!isCameraOn && (
        <ParticipantCameraOffBox
          colorCode={colorCode}
          latter={latter}
          isAudioOn={isAudioOn}
          userNameValue={userNameValue}
          isLgIcon={isLgIcon}
        />
      )}
    </div>
  );
};

ParticipantGridVideo.propTypes = {
  userId: PropTypes.any, remoteStream: PropTypes.any,
  userNameValue: PropTypes.any, isAudioOn: PropTypes.any,
  isCameraOn: PropTypes.any, colorCode: PropTypes.any,
  latter: PropTypes.any, isLgIcon: PropTypes.any,
  connectionQuality: PropTypes.string,
};

export default ParticipantGridVideo;